import React, { useState, useEffect, useRef } from 'react';
import { FaChevronDown, FaTimes } from 'react-icons/fa';
import Select from 'react-select';
import BuscadorSearchHints from './BuscadorSearchHints';

const INPUT_CLASS =
  'w-full px-3 py-2.5 border border-gray-200 rounded-xl shadow-sm bg-white text-gray-900 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-shadow';

const ADVANCED_INPUT_CLASS =
  'w-full px-3 py-2.5 border border-gray-200 rounded-xl shadow-sm bg-white text-gray-900 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-shadow';

const LABEL_CLASS = 'block text-xs font-semibold text-white/90 mb-1';

const ROTACION_LABELS = {
  liquidacion: 'Liquidación',
  sin_ventas: 'Sin ventas',
  lento: 'Lento (+90d)',
  alta_rotacion: 'Alta rotación',
  normal: 'Normal',
};

const FILTER_LABELS = {
  codigo_barras: 'Código barras',
  sku: 'SKU',
  nombre_producto: 'Nombre',
  medidas: 'Medidas',
  nombre_marca: 'Marca producto',
  vehiculo: 'Vehículo',
  homonimos: 'Homónimos',
  estado_rotacion: 'Rotación',
  id_categoria: 'Categoría',
};

const isEditableTarget = (el) => {
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
};

const hasAdvancedFiltersActive = (filters, filtrosAtributos) => {
  const advancedFields = ['nombre_marca', 'homonimos', 'estado_rotacion', 'id_categoria'];
  const hasFilter = advancedFields.some((key) => String(filters[key] || '').trim() !== '');
  const hasAtributos = Object.values(filtrosAtributos || {}).some((v) => String(v || '').trim() !== '');
  return hasFilter || hasAtributos;
};

const BuscadorSearchForm = ({
  filters,
  filtrosAtributos,
  atributosDisponibles,
  vehiculos,
  categoryOptions,
  customSelectStyles,
  nombreProductoRef,
  token,
  onInputChange,
  onVehiculoChange,
  onCategoryChange,
  onAtributoChange,
  onClearFilter,
  onApplyRecentSearch,
  onApplyFrequentQuery,
  onSearch,
  onReset,
  hasActiveFilters,
}) => {
  const codigoBarrasRef = useRef(null);
  const skuRef = useRef(null);
  const medidasRef = useRef(null);
  const vehiculoRef = useRef(null);

  const [advancedOpen, setAdvancedOpen] = useState(() =>
    hasAdvancedFiltersActive(filters, filtrosAtributos)
  );

  useEffect(() => {
    nombreProductoRef?.current?.focus();
  }, [nombreProductoRef]);

  useEffect(() => {
    if (hasAdvancedFiltersActive(filters, filtrosAtributos)) {
      setAdvancedOpen(true);
    }
  }, [filters.nombre_marca, filters.homonimos, filters.estado_rotacion, filters.id_categoria, filtrosAtributos]);

  useEffect(() => {
    const handler = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      const inField = isEditableTarget(document.activeElement);
      if (mod && e.key === 'k' && !inField) {
        e.preventDefault();
        nombreProductoRef.current?.focus();
      } else if (mod && e.key === 'b' && !inField) {
        e.preventDefault();
        codigoBarrasRef.current?.focus();
      } else if (mod && e.shiftKey && (e.key === 'S' || e.key === 's') && !inField) {
        e.preventDefault();
        skuRef.current?.focus();
      } else if (mod && e.key === 'm' && !inField) {
        e.preventDefault();
        medidasRef.current?.focus();
      } else if (mod && e.key === 'v' && !inField) {
        e.preventDefault();
        vehiculoRef.current?.focus();
      } else if (e.key === 'Escape') {
        const el = document.activeElement;
        if (el && el.name && (el.tagName === 'INPUT' || el.tagName === 'SELECT')) {
          if (el.name === 'vehiculo') {
            onVehiculoChange({ target: { name: 'vehiculo', value: '' } });
          } else {
            onInputChange({ target: { name: el.name, value: '' } });
          }
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [nombreProductoRef, onInputChange, onVehiculoChange]);

  const activePills = [];

  Object.entries(filters).forEach(([key, value]) => {
    if (!value || String(value).trim() === '' || key === 'id_vehiculo') return;
    let display = value;
    if (key === 'estado_rotacion') display = ROTACION_LABELS[value] || value;
    if (key === 'id_categoria') {
      const cat = categoryOptions.find((o) => String(o.value) === String(value));
      display = cat?.label || value;
    }
    activePills.push({ key, label: FILTER_LABELS[key] || key, display });
  });

  Object.entries(filtrosAtributos).forEach(([attrId, value]) => {
    if (!value || String(value).trim() === '') return;
    const attr = atributosDisponibles.find((a) => String(a.id) === String(attrId));
    activePills.push({ key: `attr_${attrId}`, label: attr?.nombre_atributo || 'Atributo', display: value, isAttr: true, attrId });
  });

  return (
    <form onSubmit={onSearch} className="space-y-4">
      <BuscadorSearchHints
        token={token}
        filters={filters}
        vehiculos={vehiculos}
        categoryOptions={categoryOptions}
        onApplyRecentSearch={onApplyRecentSearch}
        onApplyFrequentQuery={onApplyFrequentQuery}
      />

      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 sm:p-5 border border-white/20 shadow-lg space-y-4">
        <p className="text-white/80 text-xs font-medium leading-relaxed">
          Atajos:{' '}
          <kbd className="px-1 py-0.5 rounded bg-white/20 text-[10px]">Ctrl+B</kbd> barras ·{' '}
          <kbd className="px-1 py-0.5 rounded bg-white/20 text-[10px]">Ctrl+Shift+S</kbd> SKU ·{' '}
          <kbd className="px-1 py-0.5 rounded bg-white/20 text-[10px]">Ctrl+K</kbd> nombre ·{' '}
          <kbd className="px-1 py-0.5 rounded bg-white/20 text-[10px]">Ctrl+M</kbd> medidas ·{' '}
          <kbd className="px-1 py-0.5 rounded bg-white/20 text-[10px]">Ctrl+V</kbd> vehículo ·{' '}
          <kbd className="px-1 py-0.5 rounded bg-white/20 text-[10px]">Esc</kbd> limpiar campo
        </p>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className={LABEL_CLASS} htmlFor="buscador-codigo_barras">
                <span className="sm:hidden">Cód. barras</span>
                <span className="hidden sm:inline">Código de barras</span>
              </label>
              <input
                id="buscador-codigo_barras"
                ref={codigoBarrasRef}
                type="text"
                name="codigo_barras"
                placeholder="Escanear o escribir"
                value={filters.codigo_barras}
                onChange={onInputChange}
                className={INPUT_CLASS}
              />
            </div>
            <div>
              <label className={LABEL_CLASS} htmlFor="buscador-sku">SKU</label>
              <input
                id="buscador-sku"
                ref={skuRef}
                type="text"
                name="sku"
                placeholder="SKU"
                value={filters.sku}
                onChange={onInputChange}
                className={INPUT_CLASS}
                list="buscador-hints-sku"
              />
            </div>
            <div>
              <label className={LABEL_CLASS} htmlFor="buscador-medidas">Medidas</label>
              <input
                id="buscador-medidas"
                ref={medidasRef}
                type="text"
                name="medidas"
                placeholder="Medidas"
                value={filters.medidas}
                onChange={onInputChange}
                className={INPUT_CLASS}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border-t border-white/10 pt-3">
            <div>
              <label className={LABEL_CLASS} htmlFor="buscador-nombre_producto">Nombre del producto</label>
              <input
                id="buscador-nombre_producto"
                type="text"
                ref={nombreProductoRef}
                name="nombre_producto"
                autoFocus
                placeholder="Descripción, referencia o término"
                value={filters.nombre_producto}
                onChange={onInputChange}
                className={INPUT_CLASS}
                list="buscador-hints-nombre_producto"
              />
            </div>
            <div>
              <label className={LABEL_CLASS} htmlFor="buscador-vehiculo">Vehículo</label>
              <input
                id="buscador-vehiculo"
                ref={vehiculoRef}
                list="buscador-hints-vehiculo"
                name="vehiculo"
                placeholder="Ej. Mazda 3"
                value={filters.vehiculo || ''}
                onChange={onVehiculoChange}
                className={INPUT_CLASS}
              />
            </div>
          </div>
        </div>

        {activePills.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {activePills.map((pill) => (
              <span
                key={pill.key}
                className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full bg-yellow-500/90 text-black text-xs font-semibold"
              >
                <span className="truncate max-w-[200px]">{pill.label}: {pill.display}</span>
                <button
                  type="button"
                  onClick={() => onClearFilter(pill.isAttr ? pill.attrId : pill.key, pill.isAttr)}
                  className="p-0.5 rounded-full hover:bg-black/10"
                  aria-label={`Quitar ${pill.label}`}
                >
                  <FaTimes className="text-[10px]" />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="flex flex-wrap justify-center sm:justify-end gap-3 pt-1">
          <button
            type="submit"
            className="px-8 py-2.5 rounded-xl font-bold bg-yellow-500 text-black hover:bg-yellow-400 transition-colors disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed shadow-md"
            disabled={!hasActiveFilters}
          >
            Buscar
          </button>
          <button
            type="button"
            onClick={onReset}
            className="px-6 py-2.5 rounded-xl font-bold bg-gray-800/80 text-white hover:bg-red-600 transition-colors"
          >
            Limpiar
          </button>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setAdvancedOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl bg-white/95 border border-gray-200 text-gray-800 font-semibold text-sm hover:border-yellow-400 transition-colors shadow-sm"
      >
        <span>Más filtros {advancedOpen ? '' : '(marca, rotación, categoría…)'}</span>
        <FaChevronDown className={`transition-transform ${advancedOpen ? 'rotate-180' : ''}`} />
      </button>

      {advancedOpen && (
        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-200 shadow-sm space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Marca producto</label>
              <input type="text" name="nombre_marca" placeholder="Marca producto" value={filters.nombre_marca} onChange={onInputChange} className={ADVANCED_INPUT_CLASS} list="buscador-hints-nombre_marca" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Homónimos</label>
              <input type="text" name="homonimos" placeholder="Términos alternativos" value={filters.homonimos} onChange={onInputChange} className={ADVANCED_INPUT_CLASS} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Rotación</label>
              <select
                name="estado_rotacion"
                value={filters.estado_rotacion}
                onChange={onInputChange}
                className={ADVANCED_INPUT_CLASS}
                title="Filtra en base de datos antes de buscar"
              >
                <option value="">Todas</option>
                <option value="liquidacion">Liquidación</option>
                <option value="sin_ventas">Sin ventas (nuevo)</option>
                <option value="lento">Lento (+90d)</option>
                <option value="alta_rotacion">Alta rotación</option>
                <option value="normal">Normal</option>
              </select>
            </div>
          </div>

          <div className="p-4 border border-dashed border-sky-300 rounded-xl bg-sky-50/50">
            <h3 className="text-gray-800 font-bold mb-3 text-sm">Atributos de categoría</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Select
                  options={categoryOptions}
                  value={categoryOptions.find((o) => o.value === filters.id_categoria) || null}
                  onChange={onCategoryChange}
                  placeholder="Seleccionar categoría"
                  isClearable
                  styles={customSelectStyles}
                />
              </div>
              {atributosDisponibles.map((attr) => (
                <input
                  key={attr.id}
                  type="text"
                  placeholder={attr.nombre_atributo}
                  value={filtrosAtributos[attr.id] || ''}
                  onChange={(e) => onAtributoChange(attr.id, e.target.value)}
                  className="w-full px-3 py-2.5 border border-sky-300 rounded-xl font-medium text-sm bg-white focus:ring-2 focus:ring-sky-300 focus:outline-none"
                />
              ))}
              {atributosDisponibles.length === 0 && !filters.id_categoria && (
                <p className="md:col-span-2 text-sky-700/70 text-sm italic">Selecciona una categoría para ver filtros adicionales.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </form>
  );
};

export default BuscadorSearchForm;
